import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import { useLanguage } from '../hooks/useLanguage';
import { supabase } from '../supabase/config';

// MUI components
import {
  Container,
  Typography,
  Box,
  Paper,
  Avatar,
  TextField,
  Button,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Switch,
  FormControl,
  Card,
  CardContent,
  Select,
  MenuItem
} from '@mui/material';
import {
  Save as SaveIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  PhotoCamera as PhotoCameraIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Translate as TranslateIcon
} from '@mui/icons-material';

const Profile = () => {
  const navigate = useNavigate();
  const { user, updateProfile } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const { language, changeLanguage, getAvailableLanguages, t } = useLanguage();
  
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [location, setLocation] = useState('');
  const [avatar, setAvatar] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Fetch user profile data
  const fetchProfile = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      // Check if avatars bucket exists, but don't try to create it
      // We'll assume it exists or will be created by an admin
      try {
        const { data: avatarData } = await supabase
          .storage
          .from('avatars')
          .list();
        
        console.log('Avatar bucket exists:', avatarData);
      } catch (bucketError) {
        console.log('Avatar bucket might not exist:', bucketError);
        // Continue anyway - we'll handle upload errors separately
      }
      
      // Get user profile from the users table
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();
      
      // Set display name from user metadata if available
      const metadataDisplayName = user.user_metadata?.display_name;
      
      if (error) {
        // If there's an error fetching from the database, use metadata
        console.error('Error fetching from database:', error);
        if (metadataDisplayName) {
          setDisplayName(metadataDisplayName);
        }
      } else if (data) {
        // Use database value if available, otherwise use metadata
        setDisplayName(data.display_name || metadataDisplayName || '');
        setBio(data.bio || '');
        setLocation(data.location || '');
        
        // Get avatar URL if it exists
        if (data.avatar_url) {
          try {
            const { data: avatarData } = await supabase
              .storage
              .from('avatars')
              .getPublicUrl(data.avatar_url);
            
            if (avatarData && avatarData.publicUrl) {
              setAvatarUrl(avatarData.publicUrl);
            }
          } catch (avatarError) {
            console.error('Error getting avatar URL:', avatarError);
            // Try a different approach if the first one fails
            try {
              // If the avatar_url is just a filename, try with user ID path
              if (!data.avatar_url.includes('/')) {
                const userPath = `${user.id}/${data.avatar_url}`;
                const { data: userPathData } = await supabase
                  .storage
                  .from('avatars')
                  .getPublicUrl(userPath);
                
                if (userPathData && userPathData.publicUrl) {
                  setAvatarUrl(userPathData.publicUrl);
                }
              }
            } catch (secondError) {
              console.error('Second attempt to get avatar failed:', secondError);
            }
          }
        }
      } else if (metadataDisplayName) {
        // If no data but metadata exists
        setDisplayName(metadataDisplayName);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching profile:', err);
      
      // Try to use metadata if available
      if (user.user_metadata?.display_name) {
        setDisplayName(user.user_metadata.display_name);
      }
      
      setError('Failed to load profile data');
      setLoading(false);
    }
  };
  
  // Call fetchProfile when user changes
  useEffect(() => {
    fetchProfile();
  }, [user]);
  
  // Handle avatar file selection
  const handleAvatarChange = (e) => {
    if (!e.target.files || e.target.files.length === 0) {
      return;
    }
    
    const file = e.target.files[0];
    
    // Check file size (limit to 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('Image is too large. Please select an image under 2MB.');
      return;
    }
    
    // Check file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file.');
      return;
    }
    
    const fileReader = new FileReader();
    
    fileReader.onload = (event) => {
      setAvatarUrl(event.target.result);
    };
    
    fileReader.readAsDataURL(file);
    setAvatar(file);
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) return;
    
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      // Prepare profile updates
      const updates = {
        display_name: displayName,
        bio: bio,
        location: location
      };
      
      // Upload avatar if changed
      if (avatar) {
        try {
          // Generate a unique file name
          const fileExt = avatar.name.split('.').pop();
          const fileName = `${user.id}/${Math.random().toString(36).substring(2)}.${fileExt}`;
          
          // Upload to the avatars bucket
          const { error: uploadError } = await supabase
            .storage
            .from('avatars')
            .upload(fileName, avatar, {
              cacheControl: '3600',
              upsert: true
            });
          
          if (uploadError) throw uploadError;
          
          // Get the public URL
          const { data: publicUrlData } = await supabase
            .storage
            .from('avatars')
            .getPublicUrl(fileName);
          
          if (publicUrlData) {
            // Store both the storage path and the public URL
            updates.avatar_url = fileName; // Store the path for future reference
          }
        } catch (uploadErr) {
          console.error('Avatar upload error:', uploadErr);
          setError('Failed to upload profile picture: ' + uploadErr.message);
          setSaving(false);
          return;
        }
      }
      
      // Update profile using the context function
      await updateProfile(updates);
      
      // Get the latest public URL and update the UI
      if (updates.avatar_url) {
        const { data: latestUrlData } = await supabase
          .storage
          .from('avatars')
          .getPublicUrl(updates.avatar_url);
        
        if (latestUrlData) {
          setAvatarUrl(latestUrlData.publicUrl);
        }
      }
      
      setSuccess('Profile updated successfully');
      setEditMode(false);
      setSaving(false);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile: ' + err.message);
      setSaving(false);
    }
  };
  
  // Handle password reset
  const handlePasswordReset = async () => {
    if (!user || !user.email) return;
    
    try {
      setResettingPassword(true);
      setError(null);
      setSuccess(null);
      
      const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      
      if (error) throw error;
      
      setSuccess('Password reset email sent. Please check your inbox.');
      setResettingPassword(false);
    } catch (err) {
      console.error('Error resetting password:', err);
      setError('Failed to send password reset email: ' + err.message);
      setResettingPassword(false);
    }
  };
  
  if (!user) {
    return (
      <Container maxWidth="md">
        <Box sx={{ my: 4, textAlign: 'center' }}>
          <Typography variant="h4">
            Please log in to view your profile
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => navigate('/login')}
            sx={{ mt: 2 }}
          >
            Go to Login
          </Button>
        </Box>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="md">
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 4 }}>
        {t.myProfile}
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}
      
      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
              <Box display="flex" flexDirection="column" alignItems="center">
                <Avatar
                  src={avatarUrl}
                  sx={{
                    width: 120,
                    height: 120,
                    mb: 2,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    fontSize: '3rem',
                    position: 'relative'
                  }}
                >
                  {!avatarUrl && (displayName.charAt(0).toUpperCase() || <PersonIcon fontSize="large" />)}
                  
                  {editMode && (
                    <label htmlFor="avatar-upload">
                      <input
                        accept="image/*"
                        id="avatar-upload"
                        type="file"
                        style={{ display: 'none' }}
                        onChange={handleAvatarChange}
                      />
                      <IconButton
                        component="span"
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          right: 0,
                          bgcolor: 'primary.main',
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'primary.dark',
                          },
                        }}
                      >
                        <PhotoCameraIcon />
                      </IconButton>
                    </label>
                  )}
                </Avatar>
                
                <Typography variant="h6" gutterBottom>
                  {displayName || 'User'}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {user?.email}
                </Typography>
                
                {!editMode && (
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => setEditMode(true)}
                    sx={{ mt: 2 }}
                  >
                    {t.editProfile}
                  </Button>
                )}
              </Box>
            </Paper>
            
            {/* Language Selector Card */}
            <Card sx={{ borderRadius: 3, mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t.language}
                </Typography>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center">
                    <TranslateIcon sx={{ mr: 1, color: 'primary.main' }} />
                  </Box>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={language}
                      onChange={(e) => changeLanguage(e.target.value)}
                      displayEmpty
                    >
                      {getAvailableLanguages().map((lang) => (
                        <MenuItem key={lang.code} value={lang.code}>
                          {lang.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </CardContent>
            </Card>
            
            {/* Dark Mode Toggle Card */}
            <Card sx={{ borderRadius: 3, mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t.appearance}
                </Typography>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center">
                    {darkMode ? 
                      <DarkModeIcon sx={{ mr: 1, color: 'primary.main' }} /> : 
                      <LightModeIcon sx={{ mr: 1, color: 'primary.main' }} />
                    }
                    <Typography>
                      {darkMode ? t.darkMode : t.lightMode}
                    </Typography>
                  </Box>
                  <Switch
                    checked={darkMode}
                    onChange={toggleDarkMode}
                    color="primary"
                  />
                </Box>
              </CardContent>
            </Card>
            
            {/* Password Reset Card */}
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t.security}
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                onClick={handlePasswordReset}
                disabled={resettingPassword}
                fullWidth
                sx={{ mt: 1 }}
              >
                {resettingPassword ? t.sending : t.resetPassword}
              </Button>
            </Paper>
          </Grid>
          
          {/* Profile Details */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Box component="form" onSubmit={handleSubmit}>
                <Typography variant="h6" gutterBottom>
                  {t.profileInfo}
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                <TextField
                  fullWidth
                  label={t.displayName}
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  margin="normal"
                  disabled={!editMode}
                  required
                />
                
                <TextField
                  fullWidth
                  label={t.bio}
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  margin="normal"
                  multiline
                  rows={4}
                  disabled={!editMode}
                  placeholder={editMode ? "Tell us about yourself" : "No bio provided"}
                />
                
                <TextField
                  fullWidth
                  label={t.location}
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  margin="normal"
                  disabled={!editMode}
                  placeholder={editMode ? "Your city or country" : "No location provided"}
                />
                
                {editMode && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button 
                      variant="outlined" 
                      color="secondary" 
                      onClick={() => setEditMode(false)}
                      sx={{ mr: 2 }}
                      disabled={saving}
                      type="button"
                    >
                      {t.cancel}
                    </Button>
                    <Button 
                      type="submit" 
                      variant="contained" 
                      color="primary"
                      startIcon={saving ? <CircularProgress size={24} /> : <SaveIcon />}
                      disabled={saving}
                      onClick={handleSubmit}
                    >
                      {saving ? t.saving : t.saveChanges}
                    </Button>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default Profile; 